import { createContext } from "react";

import type { IAdvisor } from "@/components/ai-assistant/advisor";
import type { MayaFormSpec } from "@/components/ai-assistant/maya-form";
import type { IChatAttachment } from "@/types/(my-space)/ai-assistant.interface";

import type { IMayaError, IMayaMessage, IMayaPageContext } from "../types";

export interface ChatContextValue {
  messages: IMayaMessage[];
  isTyping: boolean;
  error: IMayaError | null;
  /** Backend conversation id — shared with the full AI Assistant page. */
  sessionId: string | null;
  /** When true, Maya's responses are read aloud via text-to-speech. */
  readAloud: boolean;
  /** The page open beside the sidebar (sent when the sidebar's
   *  `sharePageContext` is on). */
  pageContext: IMayaPageContext;
  /** The AI's current step while a reply streams, e.g. "Checking payroll…". */
  status: string | null;
  /** The open chat has older turns that aren't loaded yet. */
  hasEarlier: boolean;
  /** A temporary chat: not saved to history. */
  temporary: boolean;
  /** Switch temporary mode — starts a fresh chat either way. */
  toggleTemporary: () => void;
  /** What Maya draws on for this chat (composer picker, starters). */
  advisor: IAdvisor;
  selectAdvisor: (slug: string) => void;
  /** The reply being regenerated, or re-answered after a prompt edit. */
  pendingReplyId: string | null;
  /** A form the latest reply asked for (merged into the draft, not sent). */
  activeForm: MayaFormSpec | null;
  closeForm: () => void;
  /** A saved chat is being opened. */
  loadingConversation: boolean;
  /** Older turns of the open chat are being fetched. */
  loadingEarlier: boolean;
  /** Send a turn: text, files already uploaded, or both. */
  sendMessage: (text: string, attachments?: IChatAttachment[]) => Promise<void>;
  retryLast: () => Promise<void>;
  resetChat: () => void;
  /** Open a saved chat — at a given prompt, if one is passed (no-op while a
   *  reply is streaming). */
  loadConversation: (sessionId: string, messageId?: number) => Promise<void>;
  /** A turn to scroll to and pulse (picked from the chat list). */
  highlightId: string | null;
  clearHighlight: () => void;
  /** Fetch the next page of older turns of the open chat. */
  loadEarlier: () => Promise<void>;
  /** Change a sent prompt; its reply is re-answered. Resolves true on success. */
  editMessage: (id: string, text: string) => Promise<boolean>;
  /** Ask for a new answer in place of a reply. */
  regenerate: (id: string) => Promise<void>;
  toggleReadAloud: () => void;
  /** Stop the in-flight response (server-side cancel + abort local stream). */
  stop: () => void;
}

export const ChatContext = createContext<ChatContextValue | null>(null);
